import React from 'react';
import {
    BookOpen, Users, Award, Play, Star, CheckCircle, ArrowRight,
    Globe, Clock, TrendingUp, User, Menu, X, Calculator, Atom,
    FlaskConical, Languages, MapPin, Phone, Mail, GraduationCap,
    Target, Trophy, Brain, Zap, Shield, LogIn, UserPlus, ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { FaFacebook, FaTwitter, FaInstagram, FaLinkedin } from 'react-icons/fa';
import Logo from '../../assets/icons/logo.svg';

const container = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
};

const fadeIn = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { duration: 0.6 } }
};

function LandingPage() {
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
    const dropdownRef = React.useRef();

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

    return (
        <div className="min-h-screen bg-white overflow-x-hidden">
            {/* Navigation */}
            <motion.nav
                initial={{ y: -100 }}
                animate={{ y: 0 }}
                transition={{ type: 'spring', stiffness: 100 }}
                className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-gray-100 z-50 shadow-sm"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            className="flex items-center space-x-2 cursor-pointer"
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        >
                            <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-lg">
                                {/* <GraduationCap className="w-6 h-6 text-white" /> */}
                                <img src={Logo} alt="Logo" />
                            </div>
                            <span className="text-2xl font-bold bg-black bg-clip-text text-transparent">
                                IntelliAcademy
                            </span>
                        </motion.div>

                        {/* Desktop Navigation */}
                        <div className="hidden md:flex items-center space-x-8">
                            <motion.a
                                whileHover={{ scale: 1.05, color: '#059669' }}
                                href="#courses"
                                className="text-gray-700 transition-colors duration-200"
                            >
                                Courses
                            </motion.a>
                            <motion.a
                                whileHover={{ scale: 1.05, color: '#059669' }}
                                href="#results"
                                className="text-gray-700 transition-colors duration-200"
                            >
                                Results
                            </motion.a>
                            <motion.a
                                whileHover={{ scale: 1.05, color: '#059669' }}
                                href="#faculty"
                                className="text-gray-700 transition-colors duration-200"
                            >
                                Faculty
                            </motion.a>
                            <motion.a
                                whileHover={{ scale: 1.05, color: '#059669' }}
                                href="#contact"
                                className="text-gray-700 transition-colors duration-200"
                            >
                                Contact
                            </motion.a>

                            <motion.div
                                className="relative"
                                ref={dropdownRef}
                            >
                                <button
                                    className="flex items-center space-x-1 text-gray-700 hover:text-green-600 transition-colors duration-200"
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    aria-expanded={isDropdownOpen}
                                    aria-haspopup="true"
                                >
                                    <LogIn className="w-4 h-4" />
                                    <span>Sign In</span>
                                    <ChevronRight className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-90' : ''}`} />
                                </button>

                                {isDropdownOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                        className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-100"
                                    >
                                        <button
                                            onClick={() => {
                                                navigate('/login?role=admin');
                                                setIsDropdownOpen(false);
                                            }}
                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 w-full text-left hover:text-green-600 transition-colors"
                                        >
                                            As Admin
                                        </button>
                                        <button
                                            onClick={() => {
                                                navigate('/login?role=student');
                                                setIsDropdownOpen(false);
                                            }}
                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 w-full text-left hover:text-green-600 transition-colors"
                                        >
                                            As Student
                                        </button>
                                        <button
                                            onClick={() => {
                                                navigate('/login?role=parent');
                                                setIsDropdownOpen(false);
                                            }}
                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 w-full text-left hover:text-green-600 transition-colors"
                                        >
                                            As Parent
                                        </button>
                                        <button
                                            onClick={() => {
                                                navigate('/login?role=teacher');
                                                setIsDropdownOpen(false);
                                            }}
                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 w-full text-left hover:text-green-600 transition-colors"
                                        >
                                            As Teacher
                                        </button>
                                    </motion.div>
                                )}
                            </motion.div>

                            <motion.button
                                whileHover={{ scale: 1.05, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => navigate('/signup')}
                                className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-2 rounded-full hover:shadow-lg transition-all duration-200 flex items-center space-x-2"
                            >
                                <UserPlus className="w-4 h-4" />
                                <span>Sign Up As Student</span>
                            </motion.button>
                        </div>

                        {/* Mobile menu button */}
                        <div className="md:hidden">
                            <motion.button
                                whileTap={{ scale: 0.9 }}
                                onClick={toggleMenu}
                                className="text-gray-700 p-1"
                            >
                                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                            </motion.button>
                        </div>
                    </div>
                </div>

                {/* Mobile Navigation */}
                {isMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="md:hidden bg-white border-t border-gray-100 overflow-hidden"
                    >
                        <div className="px-4 py-4 space-y-4">
                            <a href="#courses" className="block text-gray-700 hover:text-green-600">Courses</a>
                            <a href="#results" className="block text-gray-700 hover:text-green-600">Results</a>
                            <a href="#faculty" className="block text-gray-700 hover:text-green-600">Faculty</a>
                            <a href="#contact" className="block text-gray-700 hover:text-green-600">Contact</a>

                            <div className="pt-2">
                                <button
                                    onClick={() => navigate('/login?role=admin')}
                                    className="w-full text-left text-gray-700 hover:text-green-600 py-2"
                                >
                                    Admin Sign In
                                </button>
                                <button
                                    onClick={() => navigate('/login?role=student')}
                                    className="w-full text-left text-gray-700 hover:text-green-600 py-2"
                                >
                                    Student Sign In
                                </button>
                                <button
                                    onClick={() => navigate('/login?role=parent')}
                                    className="w-full text-left text-gray-700 hover:text-green-600 py-2"
                                >
                                    Parent Sign In
                                </button>
                                <button
                                    onClick={() => navigate('/login?role=teacher')}
                                    className="w-full text-left text-gray-700 hover:text-green-600 py-2"
                                >
                                    Teacher Sign In
                                </button>
                            </div>

                            <button
                                onClick={() => navigate('/signup')}
                                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-full mt-2"
                            >
                                Sign Up
                            </button>
                        </div>
                    </motion.div>
                )}
            </motion.nav>

            {/* Hero Section */}
            <section className="pt-24 pb-20 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-4 -right-4 w-72 h-72 bg-gradient-to-br from-green-200/30 to-emerald-200/30 rounded-full blur-3xl"></div>
                    <div className="absolute top-1/2 -left-4 w-96 h-96 bg-gradient-to-br from-amber-200/20 to-yellow-200/20 rounded-full blur-3xl"></div>
                </div>
                
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            className="space-y-8"
                        >
                            <div className="space-y-4">
                                <motion.span
                                    whileHover={{ scale: 1.05 }}
                                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 text-sm font-medium rounded-full shadow-sm"
                                >
                                    🏆 #1 Coaching Institute for Classes 9-12
                                </motion.span>
                                <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                                    Excel in Your
                                    <span className="block bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent">
                                        Board Exams
                                    </span>
                                </h1>
                                <p className="text-xl text-gray-600 leading-relaxed">
                                    Join 50,000+ successful students who achieved 90+ marks in their board exams. Expert faculty, proven methods, and personalized attention for Classes 9, 10, 11 & 12.
                                </p>
                            </div>

                            <div className="flex items-center space-x-8 pt-4">
                                {[
                                    { value: "50K+", label: "Students" },
                                    { value: "95%", label: "Pass Rate" },
                                    { value: "15+", label: "Years Experience" }
                                ].map((stat, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 + 0.5 }}
                                        className="text-center"
                                    >
                                        <div className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">{stat.value}</div>
                                        <div className="text-gray-600">{stat.label}</div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            className="relative"
                        >
                            <div className="relative z-10 bg-white rounded-2xl shadow-2xl p-8 border border-green-100">
                                <div className="space-y-6">
                                    <div className="flex items-center space-x-4">
                                        <motion.div
                                            animate={{ rotate: [0, 10, -10, 0] }}
                                            transition={{ repeat: Infinity, duration: 3 }}
                                            className="w-12 h-12 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full flex items-center justify-center shadow-lg"
                                        >
                                            <Trophy className="w-6 h-6 text-white" />
                                        </motion.div>
                                        <div>
                                            <div className="font-semibold text-gray-900">Rizwan Ahmed</div>
                                            <div className="text-gray-500">Class 12 - 96% in Boards</div>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        {[
                                            { subject: "Physics", score: 98, color: "from-green-500 to-emerald-500" },
                                            { subject: "Mathematics", score: 95, color: "from-emerald-500 to-teal-500" },
                                            { subject: "Chemistry", score: 94, color: "from-teal-500 to-cyan-500" }
                                        ].map((item, index) => (
                                            <motion.div
                                                key={index}
                                                whileHover={{ scale: 1.02 }}
                                                className="bg-gradient-to-r from-gray-50 to-green-50 rounded-lg p-4 border border-green-100"
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="font-medium">{item.subject}</span>
                                                    <span className="text-green-600 font-semibold">{item.score}%</span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                    <div
                                                        className={`bg-gradient-to-r ${item.color} h-2 rounded-full shadow-sm`}
                                                        style={{ width: `${item.score}%` }}
                                                    ></div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="flex -space-x-2">
                                            {[1, 2, 3, 4].map((i) => (
                                                <motion.img
                                                    key={i}
                                                    whileHover={{ y: -5 }}
                                                    src={`https://randomuser.me/api/portraits/thumb/men/${i}.jpg`}
                                                    alt={`Topper ${i}`}
                                                    className="w-8 h-8 rounded-full border-2 border-white object-cover shadow-sm"
                                                />
                                            ))}
                                        </div>
                                        <span className="text-sm text-gray-600">100+ toppers this year</span>
                                    </div>
                                </div>
                            </div>
                            <motion.div
                                animate={{ rotate: [0, 3, -3, 0] }}
                                transition={{ repeat: Infinity, duration: 6 }}
                                className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-400 rounded-2xl transform rotate-3 -z-10 shadow-lg"
                            ></motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <motion.section
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="py-16 bg-white"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            { icon: Users, value: "50K+", label: "Students Enrolled", color: "from-green-500 to-emerald-500" },
                            { icon: Trophy, value: "95%", label: "Success Rate", color: "from-amber-500 to-yellow-500" },
                            { icon: Award, value: "100+", label: "Expert Faculty", color: "from-teal-500 to-cyan-500" },
                            { icon: Target, value: "15+", label: "Years Experience", color: "from-emerald-500 to-green-600" }
                        ].map((stat, index) => (
                            <motion.div
                                key={index}
                                whileHover={{ y: -5 }}
                                className="text-center"
                            >
                                <div className={`w-16 h-16 bg-gradient-to-r ${stat.color} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                                    <stat.icon className="w-8 h-8 text-white" />
                                </div>
                                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                                <div className="text-gray-600">{stat.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.section>

            {/* Subjects Section */}
            <section id="courses" className="py-20 bg-gradient-to-br from-gray-50 to-green-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">
                            Master All Subjects with
                            <span className="bg-gradient-to-r from-green-600 to-emerald-700 bg-clip-text text-transparent"> Expert Guidance</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Comprehensive coaching for all major subjects across Classes 9-12 with proven teaching methodologies.
                        </p>
                    </motion.div>

                    <motion.div
                        variants={container}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true }}
                        className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12"
                    >
                        {[
                            {
                                icon: Calculator,
                                subject: "Mathematics",
                                description: "Advanced problem solving, calculus, algebra, and geometry",
                                classes: "Classes 9-12",
                                color: "from-green-500 to-emerald-500"
                            },
                            {
                                icon: Atom,
                                subject: "Physics",
                                description: "Mechanics, thermodynamics, optics, and modern physics",
                                classes: "Classes 9-12",
                                color: "from-teal-500 to-cyan-500"
                            },
                            {
                                icon: FlaskConical,
                                subject: "Chemistry",
                                description: "Organic, inorganic, and physical chemistry concepts",
                                classes: "Classes 9-12",
                                color: "from-emerald-500 to-green-600"
                            },
                            {
                                icon: Languages,
                                subject: "English",
                                description: "Literature, grammar, writing skills, and comprehension",
                                classes: "Classes 9-12",
                                color: "from-amber-500 to-yellow-500"
                            }
                        ].map((subject, index) => (
                            <motion.div
                                key={index}
                                variants={item}
                                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
                            >
                                <div className="p-6">
                                    <motion.div
                                        whileHover={{ rotate: 15, scale: 1.1 }}
                                        className={`w-16 h-16 bg-gradient-to-r ${subject.color} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}
                                    >
                                        <subject.icon className="w-8 h-8 text-white" />
                                    </motion.div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">{subject.subject}</h3>
                                    <p className="text-gray-600 mb-4 text-center text-sm">{subject.description}</p>
                                    <div className="text-center">
                                        <span className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                                            {subject.classes}
                                        </span>
                                    </div>
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="w-full mt-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-2 rounded-full font-semibold hover:shadow-lg transition-all duration-200"
                                    >
                                        View Details
                                    </motion.button>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Class-wise Programs */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
                    >
                        {[
                            { class: "Class 9", students: "12,500+", focus: "Foundation Building" },
                            { class: "Class 10", students: "15,200+", focus: "Board Preparation" },
                            { class: "Class 11", students: "10,800+", focus: "Stream Selection" },
                            { class: "Class 12", students: "11,500+", focus: "Competitive Exams" }
                        ].map((program, index) => (
                            <motion.div
                                key={index}
                                whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-green-100"
                            >
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">{program.class}</h3>
                                <p className="text-green-600 font-semibold mb-1">{program.students} enrolled</p>
                                <p className="text-gray-600 text-sm">{program.focus}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">
                            Why Choose
                            <span className="bg-gradient-to-r from-green-600 to-emerald-700 bg-clip-text text-transparent"> IntelliAcademy?</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            We provide comprehensive coaching with proven methods to ensure your success in board exams and competitive tests.
                        </p>
                    </motion.div>

                    <motion.div
                        variants={container}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true }}
                        className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
                    >
                        {[
                            {
                                icon: Brain,
                                title: "Smart Learning",
                                description: "AI-powered personalized learning paths adapted to your pace and style.",
                                color: "from-green-500 to-emerald-500"
                            },
                            {
                                icon: Users,
                                title: "Expert Faculty",
                                description: "Learn from experienced teachers with proven track records in board exams.",
                                color: "from-emerald-500 to-teal-500"
                            },
                            {
                                icon: Target,
                                title: "Focused Approach",
                                description: "Targeted preparation for board exams with regular mock tests and assessments.",
                                color: "from-teal-500 to-cyan-500"
                            },
                            {
                                icon: Clock,
                                title: "Flexible Timings",
                                description: "Multiple batch timings to fit your schedule with weekend classes available.",
                                color: "from-amber-500 to-yellow-500"
                            },
                            {
                                icon: Zap,
                                title: "Quick Doubt Resolution",
                                description: "24/7 doubt clearing sessions with dedicated faculty support.",
                                color: "from-green-500 to-emerald-600"
                            },
                            {
                                icon: Shield,
                                title: "Guaranteed Results",
                                description: "Money-back guarantee if you don't achieve your target score.",
                                color: "from-emerald-500 to-green-600"
                            }
                        ].map((feature, index) => (
                            <motion.div
                                key={index}
                                variants={item}
                                whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                                className="text-center p-6 rounded-2xl bg-white hover:bg-gradient-to-br hover:from-white hover:to-green-50 transition-all duration-300 border border-gray-100 hover:border-green-200"
                            >
                                <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                                    <feature.icon className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                                <p className="text-gray-600">{feature.description}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Results Section */}
            <section id="results" className="py-20 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">
                            Outstanding Results from Our
                            <span className="bg-gradient-to-r from-green-600 to-emerald-700 bg-clip-text text-transparent"> Star Students</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            See how our students consistently achieve 90+ marks in their board exams and secure admissions in top colleges.
                        </p>
                    </motion.div>

                    <Swiper
                        modules={[Pagination, Autoplay]}
                        spaceBetween={30}
                        slidesPerView={1}
                        pagination={{ clickable: true }}
                        autoplay={{ delay: 5000 }}
                        breakpoints={{
                            640: { slidesPerView: 1 },
                            768: { slidesPerView: 2 },
                            1024: { slidesPerView: 3 }
                        }}
                        className="pb-12"
                    >
                        {[
                            {
                                name: "Ali",
                                class: "Class 12 - Science",
                                percentage: "97.2%",
                                achievement: "Secured admission in IIT Delhi",
                                subjects: { Math: 98, Physics: 97, Chemistry: 96 }
                            },
                            {
                                name: "Noman",
                                class: "Class 10",
                                percentage: "95.8%",
                                achievement: "School topper in Mathematics",
                                subjects: { Math: 100, Science: 96, English: 94 }
                            },
                            {
                                name: "Salman",
                                class: "Class 9 - Commerce",
                                percentage: "94.5%",
                                achievement: "Selected for CA Foundation",
                                subjects: { Accounts: 98, Economics: 95, Math: 92 }
                            },
                            {
                                name: "Fatima",
                                class: "Class 11 - Science",
                                percentage: "96.3%",
                                achievement: "National Science Olympiad Gold Medalist",
                                subjects: { Physics: 97, Chemistry: 96, Biology: 96 }
                            }
                        ].map((student, index) => (
                            <SwiperSlide key={index}>
                                <motion.div
                                    whileHover={{ y: -5 }}
                                    className="bg-white rounded-2xl p-6 shadow-lg h-full border border-green-100"
                                >
                                    <div className="flex items-center mb-4">
                                        <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold mr-4 shadow-lg">
                                            {student.name.charAt(0)}
                                        </div>
                                        <div>
                                            <div className="font-bold text-gray-900 text-lg">{student.name}</div>
                                            <div className="text-gray-600 text-sm">{student.class}</div>
                                            <div className="text-green-600 font-bold text-xl">{student.percentage}</div>
                                        </div>
                                    </div>
                                    <div className="mb-4">
                                        <div className="flex mb-4">
                                            {[...Array(5)].map((_, i) => (
                                                <Star key={i} className="w-5 h-5 text-amber-400 fill-current" />
                                            ))}
                                        </div>
                                        <p className="text-gray-700 font-medium mb-2">{student.achievement}</p>
                                        <div className="space-y-2">
                                            {Object.entries(student.subjects).map(([subject, marks]) => (
                                                <div key={subject} className="flex justify-between items-center">
                                                    <span className="text-sm text-gray-600">{subject}</span>
                                                    <span className="text-sm font-semibold text-green-600">{marks}%</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </motion.div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </section>

            {/* Faculty Section */}
            <section id="faculty" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">
                            Learn from the
                            <span className="bg-gradient-to-r from-green-600 to-emerald-700 bg-clip-text text-transparent"> Best Faculty</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Our experienced teachers have helped thousands of students achieve their academic goals.
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            {
                                name: "Dr. Danish Sheikh",
                                subject: "Mathematics",
                                experience: "15+ Years",
                                qualification: "Ph.D. in Mathematics",
                                color: "from-green-500 to-emerald-500"
                            },
                            {
                                name: "Prof. Ghazi",
                                subject: "Physics",
                                experience: "12+ Years",
                                qualification: "M.Sc. Physics, B.Ed.",
                                color: "from-teal-500 to-cyan-500"
                            },
                            {
                                name: "Dr. Faisal",
                                subject: "Chemistry",
                                experience: "18+ Years",
                                qualification: "Ph.D. in Chemistry",
                                color: "from-emerald-500 to-green-600"
                            },
                            {
                                name: "Prof. Amjad",
                                subject: "English",
                                experience: "10+ Years",
                                qualification: "M.A. English Literature",
                                color: "from-amber-500 to-yellow-500"
                            }
                        ].map((teacher, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                whileHover={{ scale: 1.03 }}
                                className="bg-gradient-to-br from-gray-50 to-green-50 rounded-2xl p-6 text-center hover:shadow-lg transition-all duration-300 overflow-hidden border border-green-100"
                            >
                                <div className={`w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden border-4 border-white shadow-lg bg-gradient-to-br ${teacher.color} flex items-center justify-center`}>
                                    <span className="text-4xl font-bold text-white">
                                        {teacher.name.charAt(0)}
                                    </span>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-1">{teacher.name}</h3>
                                <p className="text-green-600 font-semibold mb-1">{teacher.subject}</p>
                                <p className="text-gray-600 text-sm mb-1">{teacher.experience}</p>
                                <p className="text-gray-500 text-xs">{teacher.qualification}</p>

                                <div className="mt-4 flex justify-center space-x-2">
                                    <motion.button
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 hover:bg-green-200 transition-colors"
                                    >
                                        <Mail className="w-4 h-4" />
                                    </motion.button>
                                    <motion.button
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 hover:bg-emerald-200 transition-colors"
                                    >
                                        <BookOpen className="w-4 h-4" />
                                    </motion.button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials Carousel */}
            <section className="py-20 bg-gradient-to-br from-gray-50 to-green-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">
                            What Our
                            <span className="bg-gradient-to-r from-green-600 to-emerald-700 bg-clip-text text-transparent"> Students Say</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Hear from our students and parents about their experiences with IntelliAcademy.
                        </p>
                    </motion.div>

                    <Swiper
                        modules={[Pagination, Autoplay]}
                        spaceBetween={30}
                        slidesPerView={1}
                        pagination={{ clickable: true }}
                        autoplay={{ delay: 6000 }}
                        breakpoints={{
                            768: { slidesPerView: 2 },
                            1024: { slidesPerView: 3 }
                        }}
                        className="pb-12"
                    >
                        {[
                            {
                                name: "Ali's Parent",
                                role: "Father of Class 10 Student",
                                quote: "My son's performance improved dramatically after joining IntelliAcademy. His confidence in Mathematics has soared.",
                                rating: 5
                            },
                            {
                                name: "Noman",
                                role: "Class 12 Student",
                                quote: "The faculty here is exceptional. They don't just teach, they inspire. I scored 96% in my boards!",
                                rating: 5
                            },
                            {
                                name: "Fatima",
                                role: "Class 11 Student",
                                quote: "The doubt clearing sessions are a game-changer. I never have to wait long to get my questions answered.",
                                rating: 4
                            },
                            {
                                name: "Salman's Mother",
                                role: "Mother of Class 9 Student",
                                quote: "The regular progress reports and parent-teacher meetings keep us informed about our child's development.",
                                rating: 5
                            }
                        ].map((testimonial, index) => (
                            <SwiperSlide key={index}>
                                <motion.div
                                    whileHover={{ y: -5 }}
                                    className="bg-white rounded-2xl p-6 shadow-lg h-full flex flex-col border border-green-100"
                                >
                                    <div className="flex mb-4">
                                        {[...Array(testimonial.rating)].map((_, i) => (
                                            <Star key={i} className="w-5 h-5 text-amber-400 fill-current" />
                                        ))}
                                        {[...Array(5 - testimonial.rating)].map((_, i) => (
                                            <Star key={i + testimonial.rating} className="w-5 h-5 text-gray-300" />
                                        ))}
                                    </div>
                                    <blockquote className="text-gray-700 italic mb-6 flex-grow">
                                        "{testimonial.quote}"
                                    </blockquote>
                                    <div className="mt-auto">
                                        <div className="font-bold text-gray-900">{testimonial.name}</div>
                                        <div className="text-gray-600 text-sm">{testimonial.role}</div>
                                    </div>
                                </motion.div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 relative overflow-hidden">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="absolute top-0 left-0 w-full h-full">
                    <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full"></div>
                    <div className="absolute bottom-10 right-10 w-32 h-32 bg-white/5 rounded-full"></div>
                    <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-amber-400/20 rounded-full"></div>
                </div>
                
                <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl font-bold text-white mb-4">
                            Ready to Excel in Your Board Exams?
                        </h2>
                        <p className="text-xl text-green-100 mb-8">
                            Join thousands of successful students who achieved 90+ marks with our proven coaching methods.
                            Start your journey to academic excellence today!
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <motion.button
                                whileHover={{ scale: 1.05, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)' }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => navigate('/signup')}
                                className="bg-white text-green-600 px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transition-all duration-200"
                            >
                                Book Free Demo Class
                            </motion.button>
                            <motion.button
                                whileHover={{ scale: 1.05, backgroundColor: '#ffffff', color: '#059669' }}
                                whileTap={{ scale: 0.98 }}
                                className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-200"
                            >
                                Download Brochure
                            </motion.button>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-20 bg-gradient-to-br from-gray-50 to-green-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">
                            Get in Touch
                        </h2>
                        <p className="text-xl text-gray-600">
                            Visit our centers or contact us for more information about our coaching programs.
                        </p>
                    </motion.div>

                    <motion.div
                        variants={container}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true }}
                        className="grid md:grid-cols-3 gap-8"
                    >
                        {[
                            {
                                icon: MapPin,
                                title: "Visit Us",
                                details: "Gulshan e Iqbal Block 13, Karachi\nNear Aladin Park",
                            },
                            {
                                icon: Phone,
                                title: "Call Us",
                                details: "+92 321 2376993\n+92 2376 993",
                                color: "from-teal-500 to-cyan-500"
                            },
                            {
                                icon: Mail,
                                title: "Email Us",
                                details: "info@intelliacademy.com\nadmissions@intelliacademy.com",
                                color: "from-emerald-500 to-green-600"
                            }
                        ].map((contact, index) => (
                            <motion.div
                                key={index}
                                variants={item}
                                whileHover={{ y: -5 }}
                                className="text-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-green-100"
                            >
                                <div className={`w-16 h-16 bg-gradient-to-r ${contact.color} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                                    <contact.icon className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">{contact.title}</h3>
                                <p className="text-gray-600 whitespace-pre-line">{contact.details}</p>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Contact Form */}
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="mt-16 bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto border border-green-100"
                    >
                        <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Send us a Message</h3>
                        <form className="grid md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                <input
                                    type="text"
                                    id="name"
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                                    placeholder="Your name"
                                />
                            </div>
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                                    placeholder="Your email"
                                />
                            </div>
                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                <input
                                    type="tel"
                                    id="phone"
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                                    placeholder="Your phone number"
                                />
                            </div>
                            <div>
                                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                                <select
                                    id="subject"
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                                >
                                    <option>Select a subject</option>
                                    <option>Admission Inquiry</option>
                                    <option>Course Information</option>
                                    <option>Feedback</option>
                                    <option>Other</option>
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                                <textarea
                                    id="message"
                                    rows={4}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                                    placeholder="Your message"
                                ></textarea>
                            </div>
                            <div className="md:col-span-2">
                                <motion.button
                                    whileHover={{ scale: 1.02, boxShadow: '0 5px 15px -5px rgba(0, 0, 0, 0.2)' }}
                                    whileTap={{ scale: 0.98 }}
                                    type="button"
                                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 rounded-full font-semibold hover:shadow-lg transition-all duration-200"
                                >
                                    Send Message
                                </motion.button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        variants={container}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true }}
                        className="grid md:grid-cols-4 gap-8"
                    >
                        <motion.div variants={item} className="space-y-4">
                            <div className="flex items-center space-x-2">
                                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-lg">
                                    <GraduationCap className="w-6 h-6 text-white" />
                                </div>
                                <span className="text-2xl font-bold">IntelliAcademy</span>
                            </div>
                            <p className="text-gray-400">
                                Empowering students to achieve excellence in their board exams and beyond.
                            </p>
                            <div className="flex space-x-4">
                                {[
                                    { name: 'Facebook', icon: <FaFacebook className="w-5 h-5" /> },
                                    { name: 'Twitter', icon: <FaTwitter className="w-5 h-5" /> },
                                    { name: 'Instagram', icon: <FaInstagram className="w-5 h-5" /> },
                                    { name: 'LinkedIn', icon: <FaLinkedin className="w-5 h-5" /> }
                                ].map((social, index) => (
                                    <motion.a
                                        key={index}
                                        whileHover={{ y: -3, scale: 1.1 }}
                                        href="#"
                                        className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center text-gray-300 hover:text-white hover:bg-green-600 transition-colors"
                                    >
                                        {social.icon}
                                        <span className="sr-only">{social.name}</span>
                                    </motion.a>
                                ))}
                            </div>
                        </motion.div>

                        <motion.div variants={item}>
                            <h4 className="font-semibold mb-4">Classes</h4>
                            <ul className="space-y-2 text-gray-400">
                                {['Class 9', 'Class 10', 'Class 11', 'Class 12'].map((item, index) => (
                                    <li key={index}>
                                        <motion.a
                                            whileHover={{ x: 5, color: '#ffffff' }}
                                            href="#"
                                            className="transition-colors duration-200 flex items-center"
                                        >
                                            <ChevronRight className="w-4 h-4 mr-1 text-green-500" />
                                            {item}
                                        </motion.a>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        <motion.div variants={item}>
                            <h4 className="font-semibold mb-4">Subjects</h4>
                            <ul className="space-y-2 text-gray-400">
                                {['Mathematics', 'Physics', 'Chemistry', 'English'].map((item, index) => (
                                    <li key={index}>
                                        <motion.a
                                            whileHover={{ x: 5, color: '#ffffff' }}
                                            href="#"
                                            className="transition-colors duration-200 flex items-center"
                                        >
                                            <ChevronRight className="w-4 h-4 mr-1 text-green-500" />
                                            {item}
                                        </motion.a>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        <motion.div variants={item}>
                            <h4 className="font-semibold mb-4">Quick Links</h4>
                            <ul className="space-y-2 text-gray-400">
                                {['About Us', 'Results', 'Faculty', 'Contact'].map((item, index) => (
                                    <li key={index}>
                                        <motion.a
                                            whileHover={{ x: 5, color: '#ffffff' }}
                                            href="#"
                                            className="transition-colors duration-200 flex items-center"
                                        >
                                            <ChevronRight className="w-4 h-4 mr-1 text-green-500" />
                                            {item}
                                        </motion.a>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400"
                    >
                        <p>&copy; 2025 IntelliAcademy. All rights reserved.</p>
                    </motion.div>
                </div>
            </footer>
        </div>
    );
}

export default LandingPage;